import * as React from 'react'
import { useCallback, useEffect, useState } from 'react'
import { Alert, Button, Form, Input, Modal, Select, Table, Tooltip } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { CheckOutlined, CopyOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { useCookies } from 'react-cookie'
import { useParams } from 'react-router-dom'
import { ContentHeader, PaddingBox, PageBreadcrumb, PageTitle, TabHeader } from '../../components/Wrapper'
import { RequireRole } from '../../components/RequireRole'
import {
  AdminRole,
  decodeAccessToken,
  deleteDoctor,
  DoctorSummary,
  getDoctors,
  getOrganizations,
  getOrganizationUuidByEmail,
  OrganizationSummary,
  requestVerificationCode,
  signUp,
  verifyCode,
} from '../../services/admin-auth-service'

interface AddDoctorFormValues {
  username: string
  email: string
  organizationUuid?: string
}

const getColumns = (
  deletingId: string | null,
  onDelete: (doctor: DoctorSummary) => void
): ColumnsType<DoctorSummary> => [
  { title: 'Doctor Name', dataIndex: 'username', key: 'username' },
  { title: 'Email', dataIndex: 'email', key: 'email' },
  {
    title: 'Created',
    dataIndex: 'creationTimestamptz',
    key: 'creationTimestamptz',
    render: (value: string) => (value ? dayjs(value).format('DD MMM YYYY') : '-'),
  },
  {
    title: '',
    key: 'action',
    width: '120px',
    render: (_, record) => (
      <Button
        danger
        type='text'
        icon={<DeleteOutlined />}
        loading={deletingId === record.id}
        onClick={() => onDelete(record)}>
        Delete
      </Button>
    ),
  },
]

const ManageDoctors: React.FC = () => {
  const { organizationId: routeOrganizationId } = useParams()
  const [cookies] = useCookies(['loginToken'])
  const [role, setRole] = useState<AdminRole | null>(null)
  const [email, setEmail] = useState<string | null>(null)

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [form] = Form.useForm<AddDoctorFormValues>()

  const [ownOrganizationUuid, setOwnOrganizationUuid] = useState<string | null>(null)
  const [ownUuidError, setOwnUuidError] = useState('')
  const [copied, setCopied] = useState(false)

  const [organizations, setOrganizations] = useState<OrganizationSummary[]>([])
  const [scopedOrganizationName, setScopedOrganizationName] = useState<string | null>(null)
  const [accessDenied, setAccessDenied] = useState(false)

  const [doctors, setDoctors] = useState<DoctorSummary[]>([])
  const [doctorsLoading, setDoctorsLoading] = useState(true)
  const [doctorsError, setDoctorsError] = useState('')
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const [deleteTarget, setDeleteTarget] = useState<DoctorSummary | null>(null)
  const [pendingDeleteDoctor, setPendingDeleteDoctor] = useState<DoctorSummary | null>(null)
  const [sendingCode, setSendingCode] = useState(false)
  const [verifyCodeValue, setVerifyCodeValue] = useState('')
  const [verifying, setVerifying] = useState(false)
  const [verifyError, setVerifyError] = useState('')

  const targetOrganizationUuid = routeOrganizationId ?? (role === 'ROLE_ORGANIZATION' ? ownOrganizationUuid : null)

  const copyOwnUuid = useCallback(() => {
    if (!targetOrganizationUuid) return
    navigator.clipboard.writeText(targetOrganizationUuid)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }, [targetOrganizationUuid])

  const refreshDoctors = useCallback(async (organizationUuid: string | null) => {
    setDoctorsLoading(true)
    setDoctorsError('')
    try {
      const all = await getDoctors()
      setDoctors(organizationUuid ? all.filter((doctor) => doctor.organizationUuid === organizationUuid) : all)
    } catch (e: any) {
      setDoctorsError(e.message)
    } finally {
      setDoctorsLoading(false)
    }
  }, [])

  useEffect(() => {
    const init = async () => {
      const decoded = decodeAccessToken(cookies.loginToken ?? '')
      if (!decoded?.email || !decoded?.role) {
        setOwnUuidError('Unable to identify the logged-in user.')
        setDoctorsLoading(false)
        return
      }
      setEmail(decoded.email)
      setRole(decoded.role as AdminRole)

      try {
        if (routeOrganizationId) {
          if (decoded.role === 'ROLE_ORGANIZATION') {
            const ownUuid = await getOrganizationUuidByEmail(decoded.email)
            setOwnOrganizationUuid(ownUuid)
            if (ownUuid !== routeOrganizationId) {
              setAccessDenied(true)
              setDoctorsLoading(false)
              return
            }
          }
          const orgs = await getOrganizations()
          const target = orgs.find((org) => org.id === routeOrganizationId)
          setScopedOrganizationName(target?.username ?? routeOrganizationId)
          refreshDoctors(routeOrganizationId)
          return
        }

        if (decoded.role === 'ROLE_ORGANIZATION') {
          const uuid = await getOrganizationUuidByEmail(decoded.email)
          if (!uuid) {
            setOwnUuidError('Could not find your organization UUID.')
            setDoctorsLoading(false)
            return
          }
          setOwnOrganizationUuid(uuid)
          refreshDoctors(uuid)
        } else if (decoded.role === 'ROLE_ADMIN') {
          const orgs = await getOrganizations()
          setOrganizations(orgs)
          refreshDoctors(null)
        }
      } catch (e: any) {
        setOwnUuidError(e.message)
        setDoctorsLoading(false)
      }
    }
    init()
  }, [cookies.loginToken, refreshDoctors, routeOrganizationId])

  const openModal = useCallback(() => {
    setError('')
    setSuccess('')
    setIsModalOpen(true)
  }, [])

  const closeModal = useCallback(() => {
    setIsModalOpen(false)
    form.resetFields()
  }, [form])

  const onSubmit = useCallback(
    async (values: AddDoctorFormValues) => {
      const organizationUuid = targetOrganizationUuid ?? values.organizationUuid
      if (!organizationUuid) {
        setError('Please select the organization this doctor belongs to.')
        return
      }
      setError('')
      setLoading(true)
      try {
        await signUp({
          username: values.username,
          email: values.email,
          role: 'ROLE_DOCTOR',
          organizationUuid,
        })
        setSuccess(`Doctor "${values.username}" was created successfully.`)
        form.resetFields()
        refreshDoctors(organizationUuid)
        setTimeout(() => {
          setIsModalOpen(false)
        }, 1200)
      } catch (e: any) {
        setError(e.message)
      } finally {
        setLoading(false)
      }
    },
    [form, targetOrganizationUuid, refreshDoctors]
  )

  const handleDeleteClick = useCallback((doctor: DoctorSummary) => {
    setDoctorsError('')
    setDeleteTarget(doctor)
  }, [])

  const cancelDeleteTarget = useCallback(() => {
    setDeleteTarget(null)
  }, [])

  const proceedToVerification = useCallback(async () => {
    if (!deleteTarget || !email || !role) {
      setDoctorsError('Unable to identify your email for verification.')
      return
    }
    const doctor = deleteTarget
    setDeleteTarget(null)
    setVerifyError('')
    setVerifyCodeValue('')
    setPendingDeleteDoctor(doctor)
    setSendingCode(true)
    try {
      await requestVerificationCode(email, role)
    } catch (e: any) {
      setVerifyError(e.message)
    } finally {
      setSendingCode(false)
    }
  }, [deleteTarget, email, role])

  const cancelDelete = useCallback(() => {
    setPendingDeleteDoctor(null)
    setVerifyCodeValue('')
    setVerifyError('')
  }, [])

  const confirmDeleteWithCode = useCallback(async () => {
    if (!pendingDeleteDoctor || !email || !role) return
    setVerifyError('')
    setVerifying(true)
    try {
      await verifyCode(email, role, verifyCodeValue)
      setDeletingId(pendingDeleteDoctor.id)
      await deleteDoctor(pendingDeleteDoctor.id)
      refreshDoctors(targetOrganizationUuid)
      setPendingDeleteDoctor(null)
      setVerifyCodeValue('')
    } catch (e: any) {
      setVerifyError(`${e.message} Please try again.`)
    } finally {
      setVerifying(false)
      setDeletingId(null)
    }
  }, [pendingDeleteDoctor, email, role, targetOrganizationUuid, verifyCodeValue, refreshDoctors])

  if (accessDenied) {
    return (
      <div className='dash-board-page'>
        <PaddingBox white>
          <Alert
            type='error'
            showIcon
            message="You can only manage doctors that belong to your own organization."
          />
        </PaddingBox>
      </div>
    )
  }

  const pageTitle = scopedOrganizationName ? `Manage Doctors — ${scopedOrganizationName}` : 'Manage Doctors'
  const breadcrumbPath = routeOrganizationId
    ? [
        { label: 'Home', href: '/' },
        { label: 'Manage Organizations', href: '/organizations' },
        pageTitle,
      ]
    : [{ label: 'Home', href: '/' }, 'Manage Doctors']

  return (
    <div className='dash-board-page'>
      <ContentHeader white={false} border>
        <PageBreadcrumb path={breadcrumbPath} />
        <TabHeader padding={false}>
          <PageTitle text={pageTitle} />
          <Button type='primary' icon={<PlusOutlined />} onClick={openModal}>
            Add a Doctor
          </Button>
        </TabHeader>
      </ContentHeader>

      <PaddingBox white>
        {doctorsError && <Alert style={{ marginBottom: 16 }} type='error' showIcon message={doctorsError} />}
        <Table
          columns={getColumns(deletingId, handleDeleteClick)}
          dataSource={doctors}
          loading={doctorsLoading}
          rowKey={(record) => record.id}
          locale={{ emptyText: 'No doctors yet. Click "Add a Doctor" to create one.' }}
          pagination={false}
        />
      </PaddingBox>

      <Modal title='Add a Doctor' open={isModalOpen} onCancel={closeModal} footer={null} destroyOnClose>
        {error && <Alert style={{ marginBottom: 16 }} showIcon message={error} type='error' />}
        {success && <Alert style={{ marginBottom: 16 }} showIcon message={success} type='success' />}
        {ownUuidError && <Alert style={{ marginBottom: 16 }} showIcon message={ownUuidError} type='warning' />}
        <Form form={form} layout='vertical' onFinish={onSubmit} autoComplete='off'>
          <Form.Item
            name='username'
            label='Doctor Name'
            rules={[{ required: true, message: 'Enter a doctor name.' }]}>
            <Input placeholder='Jack Smith' />
          </Form.Item>

          <Form.Item
            name='email'
            label='Email'
            rules={[{ required: true, type: 'email', message: 'Enter a valid email.' }]}>
            <Input placeholder='jacksmith@nus.edu.sg' />
          </Form.Item>

          {targetOrganizationUuid ? (
            <Form.Item
              label='Organization'
              extra={
                routeOrganizationId
                  ? 'This is the organization you are viewing, filled in automatically.'
                  : 'This is your own organization, filled in automatically.'
              }>
              <Input
                value={scopedOrganizationName ?? targetOrganizationUuid}
                disabled
                suffix={
                  <Tooltip title={copied ? 'Copied!' : 'Copy UUID'}>
                    <Button
                      type='text'
                      size='small'
                      icon={copied ? <CheckOutlined /> : <CopyOutlined />}
                      onClick={copyOwnUuid}
                    />
                  </Tooltip>
                }
              />
            </Form.Item>
          ) : (
            <Form.Item
              name='organizationUuid'
              label='Organization'
              rules={[{ required: true, message: 'Select the organization this doctor belongs to.' }]}>
              <Select
                placeholder='Select an organization'
                options={organizations.map((org) => ({ label: org.username, value: org.id }))}
              />
            </Form.Item>
          )}

          <Form.Item style={{ marginBottom: 0 }}>
            <Button block type='primary' htmlType='submit' loading={loading} disabled={!targetOrganizationUuid && organizations.length === 0}>
              Create Doctor
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title='Delete this doctor?'
        open={!!deleteTarget}
        onCancel={cancelDeleteTarget}
        okText='Send code'
        okButtonProps={{ danger: true }}
        onOk={proceedToVerification}
        centered>
        <p>
          {`This will permanently delete "${deleteTarget?.username}". This cannot be undone. We'll email you a code to confirm.`}
        </p>
      </Modal>

      <Modal
        title='Confirm deletion'
        open={!!pendingDeleteDoctor}
        onCancel={cancelDelete}
        centered
        footer={null}
        destroyOnClose>
        {verifyError && <Alert style={{ marginBottom: 16 }} showIcon message={verifyError} type='error' />}
        {sendingCode ? (
          <Alert showIcon message={`Sending a verification code to ${email}…`} type='info' />
        ) : (
          <>
            <Alert
              style={{ marginBottom: 16 }}
              showIcon
              type='info'
              message={`A verification code was sent to ${email}. Enter it below to permanently delete "${pendingDeleteDoctor?.username}".`}
            />
            <Input
              placeholder='6-digit code'
              maxLength={6}
              value={verifyCodeValue}
              onChange={(e) => setVerifyCodeValue(e.target.value)}
              onPressEnter={confirmDeleteWithCode}
              style={{ marginBottom: 16 }}
            />
            <Button
              block
              danger
              type='primary'
              loading={verifying}
              disabled={verifyCodeValue.length === 0}
              onClick={confirmDeleteWithCode}>
              Verify & Delete
            </Button>
          </>
        )}
      </Modal>
    </div>
  )
}

const ManageDoctorsPage: React.FC = () => (
  <RequireRole allow={['ROLE_ADMIN', 'ROLE_ORGANIZATION']}>
    <ManageDoctors />
  </RequireRole>
)

export default ManageDoctorsPage
